import { memo, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { tableHeaderCellStyles } from "@/components/GenericTable/styles";

import {
  TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD,
  partitionTemplateRowsByShare,
  templateSendSharePercent,
} from "../helpers/buildTemplateSendSummary";
import { useTablePagination } from "../hooks/useTablePagination";
import type { TemplateSendAggregateRow } from "../types";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankRowSx,
  communicationsRankTableBodyCellSx,
  communicationsRankTableToolbarSx,
} from "./communicationsSubsectionStyles";

export interface CommunicationsTemplateSendRankTableProps {
  templateRows: TemplateSendAggregateRow[];
  totalSends: number;
  selectedTemplateCode: string | null;
  onSelectTemplateCode: (code: string | null) => void;
}

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Ranking de modelos por volume — principais (≥10%) em destaque e cauda longa paginada.
 */
const CommunicationsTemplateSendRankTable = memo(
  ({
    templateRows,
    totalSends,
    selectedTemplateCode,
    onSelectTemplateCode,
  }: CommunicationsTemplateSendRankTableProps) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredRows = useMemo(() => {
      const q = normalizeSearch(searchQuery);
      if (!q) return templateRows;
      return templateRows.filter((row) =>
        row.templateLabel.toLowerCase().includes(q),
      );
    }, [searchQuery, templateRows]);

    const { prominent, longTail } = useMemo(
      () => partitionTemplateRowsByShare(filteredRows, totalSends),
      [filteredRows, totalSends],
    );

    const longTailResetKey = useMemo(
      () => `${longTail.length}-${searchQuery}`,
      [longTail.length, searchQuery],
    );

    const {
      paginatedItems: paginatedLongTail,
      page,
      rowsPerPage,
      totalCount: longTailCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(longTail, longTailResetKey, {
      defaultRowsPerPage: 10,
    });

    const handleRowClick = (row: TemplateSendAggregateRow) => {
      const selected = row.templateCode === selectedTemplateCode;
      onSelectTemplateCode(selected ? null : row.templateCode);
    };

    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={communicationsRankTableToolbarSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Todos os modelos
            </Typography>
            <TextField
              fullWidth
              variant="filled"
              label="Filtrar modelos"
              placeholder="Busque por nome ou conteúdo do modelo"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={communicationsRankFilledSearchFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>

        {filteredRows.length === 0 ? (
          <Box sx={{ px: 1.5, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim()
                ? "Nenhum modelo corresponde à busca."
                : "Nenhum envio registrado ainda."}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table aria-label="Ranking de modelos por volume de envios">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellStyles} align="center">
                      <Typography variant="subtitle2">Modelo</Typography>
                    </TableCell>
                    <TableCell sx={tableHeaderCellStyles} align="center">
                      <Typography variant="subtitle2">Envios</Typography>
                    </TableCell>
                    <TableCell sx={tableHeaderCellStyles} align="center">
                      <Typography variant="subtitle2">Participação</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prominent.length === 0 && longTail.length > 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          bgcolor: "action.hover",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          py: 1,
                          px: 1.5,
                          ...communicationsRankTableBodyCellSx,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          Nenhum modelo atinge{" "}
                          {TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD}% dos envios
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {prominent.map((row) => {
                    const selected = row.templateCode === selectedTemplateCode;
                    const sharePercent = templateSendSharePercent(
                      row.sendCount,
                      totalSends,
                    );

                    return (
                      <TableRow
                        key={row.templateCode}
                        hover
                        selected={selected}
                        sx={{
                          ...communicationsRankRowSx(selected),
                          cursor: "pointer",
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Tooltip title={row.templateLabel}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{
                                maxWidth: { xs: 160, sm: 280, md: 360 },
                                mx: "auto",
                              }}
                            >
                              {row.templateLabel}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {row.sendCount}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%", maxWidth: 200, mx: "auto" }}
                          >
                            <Box sx={{ flex: 1, maxWidth: 120 }}>
                              <LinearProgress
                                variant="determinate"
                                value={sharePercent}
                                sx={{ height: 6, borderRadius: 999 }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{
                                fontVariantNumeric: "tabular-nums",
                                minWidth: 40,
                              }}
                            >
                              {sharePercent}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {prominent.length > 0 && longTail.length > 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{
                          bgcolor: "action.hover",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          py: 1,
                          px: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          Demais modelos (participação abaixo de{" "}
                          {TEMPLATE_SEND_PROMINENT_SHARE_THRESHOLD}%)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {paginatedLongTail.map((row) => {
                    const selected = row.templateCode === selectedTemplateCode;

                    return (
                      <TableRow
                        key={row.templateCode}
                        hover
                        selected={selected}
                        sx={{
                          ...communicationsRankRowSx(selected),
                          cursor: "pointer",
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Tooltip title={row.templateLabel}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={400}
                              noWrap
                              sx={{
                                maxWidth: { xs: 160, sm: 280, md: 360 },
                                mx: "auto",
                              }}
                            >
                              {row.templateLabel}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={400}
                          >
                            {row.sendCount}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Baixa participação
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {longTail.length > 0 ? (
              <Box
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <CommunicationsTablePagination
                  count={longTailCount}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Box>
            ) : null}
          </>
        )}
      </Paper>
    );
  },
);

CommunicationsTemplateSendRankTable.displayName =
  "CommunicationsTemplateSendRankTable";

export default CommunicationsTemplateSendRankTable;
